/**
 * AssessmentRecommendationInsert — the "Add a vetted recommendation"
 * insert action inside the draft's Recommendations section (2026-07
 * redesign; the standalone always-open library card retired).
 *
 * Opens the RecommendationPicker inline, pre-merged with the client
 * block's name/pronouns (inherited via props); insertion is one click,
 * never a paste. Inserting a topic the report already covers offers
 * replace-or-keep before anything lands; the held insert keeps the source
 * the picker gave it (including the pronoun choice it was worded with), so
 * a choice changed while the confirm is open re-words it once it lands.
 */

import { type ReactNode, useState } from 'react';
import { Lightbulb } from 'lucide-react';
import type { SpicePronounChoice } from '@/lib/api';
import type { RecommendationSource } from '@/lib/recommendationText';
import { RecommendationPicker } from './RecommendationPicker';
import { Button } from '@/components/primitives/Button';

export interface PendingInsert {
  text: string;
  topic: string;
  needsResources: boolean;
  source: RecommendationSource | undefined;
  /** The existing recommendation label/topic that already covers this. */
  matchedTopic: string;
}

export interface AssessmentRecommendationInsertProps {
  tenantSlug: string;
  clientFirstName: string;
  pronouns?: SpicePronounChoice;
  /** Passed to the picker: the library waits for a pronoun choice. */
  pronounsNeeded?: boolean;
  /** Passed to the picker: what its pronoun prompt says. */
  pronounPromptText?: string;
  /**
   * Topics/labels already in the report (model recommendations + inserted
   * ones) for the replace-or-keep check.
   */
  existingTopics: readonly string[];
  /** Insert the block with its source; replaceTopic names an existing entry to swap out. */
  onInsert: (
    text: string,
    topic: string,
    needsResources: boolean,
    source: RecommendationSource | undefined,
    replaceTopic?: string,
  ) => void;
  disabled?: boolean;
  /**
   * Controlled open state (founder polish 2026-07-06, item 4): the page
   * owns it so the sidebar outline entry can open the SAME picker.
   * Absent = internal state (standalone/test use).
   */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

function normalizeTopic(t: string): string {
  return t.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}

/** The existing topic this one already covers, or null. */
function coveredBy(topic: string, existing: readonly string[]): string | null {
  const n = normalizeTopic(topic);
  if (n.length === 0) return null;
  for (const e of existing) {
    const ne = normalizeTopic(e);
    if (ne.length === 0) continue;
    if (ne === n || ne.includes(n) || n.includes(ne)) return e;
  }
  return null;
}

export function AssessmentRecommendationInsert({
  tenantSlug,
  clientFirstName,
  pronouns,
  pronounsNeeded,
  pronounPromptText,
  existingTopics,
  onInsert,
  disabled = false,
  open: openProp,
  onOpenChange,
}: AssessmentRecommendationInsertProps): ReactNode {
  const [internalOpen, setInternalOpen] = useState(false);
  const open = openProp ?? internalOpen;
  const setOpen = onOpenChange ?? setInternalOpen;
  const [pending, setPending] = useState<PendingInsert | null>(null);

  // Returns false when the insert is deferred to the replace-or-keep
  // confirm (the picker skips its "Added" flash in that case).
  const handleAdd = (
    text: string,
    topic: string,
    needsResources: boolean,
    source: RecommendationSource | undefined,
  ): boolean | undefined => {
    const matchedTopic = coveredBy(topic, existingTopics);
    if (matchedTopic !== null) {
      setPending({ text, topic, needsResources, source, matchedTopic });
      return false;
    }
    onInsert(text, topic, needsResources, source);
    return undefined;
  };

  const resolvePending = (replace: boolean): void => {
    if (pending === null) return;
    onInsert(
      pending.text,
      pending.topic,
      pending.needsResources,
      pending.source,
      replace ? pending.matchedTopic : undefined,
    );
    setPending(null);
  };

  return (
    <div id="assessment-recommendation-insert" data-testid="assessment-recommendation-insert">
      {pending !== null && (
        <div
          className="mb-3 flex flex-wrap items-center gap-2 rounded-[12px] border border-warm-200 bg-warm-50 px-3 py-2"
          data-testid="assessment-insert-replace-or-keep"
          role="alertdialog"
          aria-label="This topic looks covered already"
        >
          <span className="text-sm text-ink">
            The report already covers <span className="font-semibold">{pending.matchedTopic}</span>.
            Replace it with <span className="font-semibold">{pending.topic}</span>, or keep both?
          </span>
          <Button
            size="sm"
            onClick={() => resolvePending(true)}
            data-testid="assessment-insert-replace"
          >
            Replace
          </Button>
          <Button
            size="sm"
            variant="secondary"
            onClick={() => resolvePending(false)}
            data-testid="assessment-insert-keep-both"
          >
            Keep both
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setPending(null)}
            data-testid="assessment-insert-cancel"
          >
            Cancel
          </Button>
        </div>
      )}

      {/* FOUNDER RULING 2026-07-06: gold-as-interactive is an explicit
          exception here (the DL working register otherwise reserves gold
          for awaiting-confirm). The founder missed this affordance
          entirely and ruled it prominent: bold, gold accent, lightbulb
          icon. Do not "fix" the gold back to sage in a register sweep. */}
      <Button
        variant="secondary"
        size="sm"
        icon={<Lightbulb size={15} aria-hidden="true" />}
        onClick={() => setOpen(!open)}
        disabled={disabled}
        aria-expanded={open}
        className="font-bold !border-warm-300 !bg-warm-50 !text-warm-700 hover:!border-warm-400 hover:!bg-warm-100"
        data-testid="assessment-add-recommendation-btn"
      >
        {open ? 'Close the library' : 'Add a vetted recommendation'}
      </Button>

      {open && (
        <div className="mt-3" data-testid="assessment-recommendation-insert-panel">
          <RecommendationPicker
            tenantSlug={tenantSlug}
            clientFirstName={clientFirstName}
            pronouns={pronouns}
            pronounsNeeded={pronounsNeeded}
            pronounPromptText={pronounPromptText}
            onAddToReport={handleAdd}
          />
        </div>
      )}
    </div>
  );
}
